import React, {useState, useEffect, useContext} from 'react';
import {Modal, ModalHeader, ModalBody, Card, CardBody, Input, Table} from 'reactstrap';
import PaginationWrapper from 'components/PaginationWrapper';
import {getUsersByOrganization, getAllProducts} from 'utils/api';
import sortAlphabetically from 'helpers/sortAlphabetically';
import {ErrorContext} from 'contexts/ErrorContext';
import Loader from 'components/Loader';
import {sortByValue} from 'helpers/sortByValue';
import SortableTableHeader from 'components/SortableTableHeader';
import UsersModalListItem from './UsersModalListItem';

function OrgUsersModal({isOpen, toggle, orgId, orgName}) {
    const {handleErrors} = useContext(ErrorContext);

    const [searchValue, setSearchValue] = useState('');
    const [fullUsersList, setFullUsersList] = useState(null);
    const [visibleList, setVisibleList] = useState(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const [fetchingData, setFetchingData] = useState(true);
    const [activeSortProperty, setActiveSortProperty] = useState('name');
    const [activeSortDirection, setActiveSortDirection] = useState('asc');
    const [activeMobileSortSelection, setActiveMobileSortSelection] = useState('name-asc');
    const [productsList, setProductsList] = useState([]);

    useEffect(() => {
        fetchOrganizationUsersList();
        fetchProductsList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(fullUsersList && visibleList) {
            applyFilters(true, fullUsersList);
        }
    }, [activeSortProperty, activeSortDirection, searchValue]);

    const applyFilters = (resetPage, list) => {
        let newArr = sortByValue(list, activeSortProperty, activeSortDirection);
        if(searchValue && searchValue.length > 0) {
            let arrFilteredBySearchValue = newArr.filter(item => {
                return item.name.toLowerCase().includes(searchValue.toLowerCase()) || item.email.toLowerCase().includes(searchValue.toLowerCase());
            });
            setVisibleList(arrFilteredBySearchValue);
            resetPage && setCurrentPageNumber(1);
        } else {
            setVisibleList(newArr);
            resetPage && setCurrentPageNumber(1);
        }
    }

    const fetchOrganizationUsersList = async(refresh) => {
        try {
            !fetchingData && !refresh && setFetchingData(true);
            let usersData = await getUsersByOrganization(orgId);
            usersData.content.result.forEach(item => {
                if(item.name === null) item.name = '';
            });
            let initialList = sortAlphabetically(usersData.content.result, 'name');
            // console.log(initialList);
            setFullUsersList(initialList);
            if(!refresh) {
                setVisibleList(initialList);
            }
            refresh && applyFilters(!refresh, initialList);
            setFetchingData(false);
        } catch(err) {
            console.log(err);
            handleErrors();
            setFetchingData(false);
        }
    };

    const fetchProductsList = async() => {
        try {
            let productsData = await getAllProducts();
            setProductsList(productsData.content.result);
        } catch(err) {
            console.log(err);
            handleErrors();
        }
    };

    const handlePaginationClick = (pageNumber) => {
        setCurrentPageNumber(pageNumber.selected + 1);
    };


    const handleSortSelect = (sortValue) => {
        if(activeSortProperty === sortValue) {
            setActiveSortDirection(activeSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setActiveSortProperty(sortValue);
            setActiveSortDirection('asc');
        }
    };

    const handleMobileSortSelect = (sortSelection) => {
        let splitArr = sortSelection.split('-');
        if(splitArr.length === 2) {
            setActiveSortProperty(splitArr[0]);
            setActiveSortDirection(splitArr[1]);
        }
        setActiveMobileSortSelection(sortSelection);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} style={{width: '98%', maxWidth: 'none', margin: '10px auto'}}>
            <ModalHeader toggle={toggle}>
                <strong>Users List: {orgName}</strong>
            </ModalHeader>
            <ModalBody>
                {visibleList === null ? (
                    <div style={{padding: '3rem 2rem'}}>
                        <Loader small text="Fetching user data"/>
                    </div>
                ) : (
                    <Card className="w-100">
                        <CardBody>
                            <div className="feeds-container_input">
                                {fullUsersList !== null && fullUsersList.length > 0 && (
                                    <div className="feeds-container-wrapper">
                                        <Input
                                            style={{height: '35px'}}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                            value={searchValue}
                                            placeholder="Search by name or email"
                                            type="text"
                                            className="searchInput"
                                        />
                                        {!searchValue ? (
                                            <i
                                                style={{
                                                    position: 'absolute',
                                                    top: '28%',
                                                    bottom: '10.5px',
                                                    right: '11px',
                                                }}
                                                className="fa fa-search search-container_icon"
                                            />
                                        ) : (
                                            <i
                                                style={{
                                                    cursor: 'pointer',
                                                    position: 'absolute',
                                                    top: '28%',
                                                    bottom: '10.5px',
                                                    right: '11px',
                                                }}
                                                onClick={() => setSearchValue('')}
                                                className="fa fa-times-circle search-container_icon"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                            {fullUsersList && fullUsersList.length > 0 && (
                                <div className="feeds_sortBy">
                                    <span>Sort by</span>
                                    <Input onChange={(e) => handleMobileSortSelect(e.target.value)} type="select"
                                           value={activeMobileSortSelection}>
                                        <option value="name-asc">Name (A to Z)</option>
                                        <option value="name-desc">Name (Z to A)</option>
                                        <option value="email-asc">Email (A to Z)</option>
                                        <option value="email-desc">Email (Z to A)</option>
                                        <option value="productName-asc">Product (A to Z)</option>
                                        <option value="productName-desc">Product (Z to A)</option>
                                        <option value="enabled-desc">Confirmed ('Yes' first)</option>
                                        <option value="enabled-asc">Confirmed ('No' first)</option>
                                        <option value="createdAt-desc">Created At (New first)</option>
                                        <option value="createdAt-asc">Created At (Old first)</option>
                                        <option value="lastLogin-desc">Last Login (New first)</option>
                                        <option value="lastLogin-asc">Last Login (Old first)</option>
                                        {/*<option value="fetchesAlertsCount">Fetches Alerts (1 to 9)</option>*/}
                                        {/*<option value="fetchesAlertsCountDescending">Fetches Alerts (9 to 1)</option>*/}
                                        <option value="searchCount-asc">Search Count (Ascending)</option>
                                        <option value="searchCount-desc">Search Count (Descending)</option>
                                    </Input>
                                </div>
                            )}
                            <div className="table mt-3">
                                {fullUsersList && fullUsersList.length > 0 ? (
                                    <Table style={{color: '#314784'}} responsive>
                                        <thead className="hidden">
                                        <tr>
                                            <SortableTableHeader
                                                name="Name"
                                                value="name"
                                                activeSortValue={activeSortProperty}
                                                activeSortDirection={activeSortDirection}
                                                handleSelect={() => handleSortSelect('name')}
                                            />
                                            <SortableTableHeader
                                                name="Email"
                                                value="email"
                                                activeSortValue={activeSortProperty}
                                                activeSortDirection={activeSortDirection}
                                                handleSelect={() => handleSortSelect('email')}
                                            />
                                            <SortableTableHeader
                                                name="Product"
                                                value="productName"
                                                activeSortValue={activeSortProperty}
                                                activeSortDirection={activeSortDirection}
                                                handleSelect={() => handleSortSelect('productName')}
                                            />
                                            <SortableTableHeader
                                                name="Confirmed"
                                                value="enabled"
                                                activeSortValue={activeSortProperty}
                                                activeSortDirection={activeSortDirection}
                                                handleSelect={() => handleSortSelect('enabled')}
                                            />
                                            <SortableTableHeader
                                                name="Created At"
                                                value="createdAt"
                                                activeSortValue={activeSortProperty}
                                                activeSortDirection={activeSortDirection}
                                                handleSelect={() => handleSortSelect('createdAt')}
                                            />
                                            <SortableTableHeader
                                                name="Last Login"
                                                value="lastLogin"
                                                activeSortValue={activeSortProperty}
                                                activeSortDirection={activeSortDirection}
                                                handleSelect={() => handleSortSelect('lastLogin')}
                                            />
                                            {/*<SortableTableHeader*/}
                                            {/*    name="Fetches Count"*/}
                                            {/*    value="fetchesCount"*/}
                                            {/*    activeSortValue={activeSortProperty}*/}
                                            {/*    activeSortDirection={activeSortDirection}*/}
                                            {/*    handleSelect={() => handleSortSelect('fetchesCount')}*/}
                                            {/*/>*/}
                                            <SortableTableHeader
                                                name="Search Count"
                                                value="searchCount"
                                                activeSortValue={activeSortProperty}
                                                activeSortDirection={activeSortDirection}
                                                handleSelect={() => handleSortSelect('searchCount')}
                                            />
                                            <th>Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {visibleList && visibleList.length > 0 ? (
                                            visibleList.slice((currentPageNumber - 1) * 50, currentPageNumber * 50).map((item) => (
                                                <UsersModalListItem
                                                    item={item}
                                                    refreshList={fetchOrganizationUsersList}
                                                    productsList={productsList}
                                                />
                                            ))
                                        ) : (
                                            <tr>
                                                <td className="mt-5">No matching results</td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <p className="mt-5">No users found</p>
                                )}
                            </div>
                            {visibleList && visibleList.length > 50 && (
                                <PaginationWrapper
                                    totalPages={Math.ceil(visibleList.length / 50)}
                                    handlePageClick={handlePaginationClick}
                                />
                            )}
                        </CardBody>
                    </Card>
                )}
            </ModalBody>
        </Modal>
    );
}

export default OrgUsersModal;